import React from 'react';

export const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';
const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_SOURCE_IMAGE_BYTES = 20 * 1024 * 1024;
const COMPRESS_TARGET_BYTES = 900 * 1024;
const COMPRESS_MAX_EDGE = 1600;
const COMPRESS_START_QUALITY = 0.86;
const COMPRESS_MIN_QUALITY = 0.72;
const COMPRESS_QUALITY_STEP = 0.07;
const COMPRESSIBLE_IMAGE_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp']);

export function getImageUrl(url) {
  if (!url) return 'https://via.placeholder.com/180x120/9b59b6/ffffff?text=Prize';
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_ORIGIN}${url}`;
}

export const getEmptyPrizeForm = () => ({
  name: '',
  description: '',
  cost: '',
  movie_ticket_cost: '',
  stock: '',
  delivery_type: 'physical',
  is_active: true,
  auto_carousel: true,
  options: []
});

export function normalizeMovieTicketCostInput(value) {
  return value === null || value === undefined ? '' : value;
}

export function normalizeOptionCostInput(value) {
  return value === null || value === undefined ? '' : value;
}

export function createEmptyOption() {
  return {
    name: '',
    description: '',
    image_url: '',
    image: null,
    image_preview_url: '',
    remove_image: false,
    cost: '',
    movie_ticket_cost: '',
    stock: '',
    is_active: true
  };
}

export function normalizeOptionsForForm(options = []) {
  return options.map((option) => ({
    id: option.id,
    name: option.name || '',
    description: option.description || '',
    image_url: option.image_url || '',
    image: null,
    image_preview_url: '',
    remove_image: false,
    cost: normalizeOptionCostInput(option.cost),
    movie_ticket_cost: normalizeOptionCostInput(option.movie_ticket_cost),
    stock: option.stock ?? '',
    is_active: option.is_active !== 0
  }));
}

export function formFromPrizeItem(item) {
  if (!item) return getEmptyPrizeForm();
  return {
    name: item.name || '',
    description: item.description || '',
    cost: item.cost ?? '',
    movie_ticket_cost: normalizeMovieTicketCostInput(item.movie_ticket_cost),
    stock: item.stock ?? '',
    delivery_type: item.delivery_type === 'virtual' ? 'virtual' : 'physical',
    is_active: Boolean(item.is_active),
    auto_carousel: item.auto_carousel !== 0,
    options: normalizeOptionsForForm(item.options || [])
  };
}

export function buildPrizePayload(form) {
  const optionsPayload = (form.options || []).map((option, index) => ({
    id: option.id,
    name: option.name.trim(),
    description: option.description || '',
    image_url: option.image_url || null,
    image: option.image || null,
    remove_image: Boolean(option.remove_image),
    cost: option.cost === '' ? null : Number(option.cost),
    movie_ticket_cost: option.movie_ticket_cost === '' ? null : Number(option.movie_ticket_cost),
    stock: Number(option.stock || 0),
    is_active: Boolean(option.is_active),
    sort_order: index
  }));

  const hasActiveOption = optionsPayload.some((option) => option.is_active);
  if (form.cost === '' && form.movie_ticket_cost === '' && !hasActiveOption) {
    throw new Error('请至少填写积分价格、电影票价格，或添加一个启用的子选项价格');
  }

  return {
    ...form,
    cost: form.cost === '' ? null : Number(form.cost),
    movie_ticket_cost: form.movie_ticket_cost === '' ? null : Number(form.movie_ticket_cost),
    stock: (form.options || []).some((option) => option.is_active)
      ? (form.options || []).filter((option) => option.is_active).reduce((sum, option) => sum + Number(option.stock || 0), 0)
      : Number(form.stock),
    delivery_type: form.delivery_type === 'virtual' ? 'virtual' : 'physical',
    options: optionsPayload
  };
}

function readImageBlob(blob, filename) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({ data_url: reader.result, filename });
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function readImageFile(file) {
  return readImageBlob(file, file.name);
}

function isCompressibleImage(file) {
  const type = (file.type || '').toLowerCase();
  return COMPRESSIBLE_IMAGE_TYPES.has(type) || /\.(jpe?g|png|webp)$/i.test(file.name || '');
}

function getCompressedFilename(filename, mimeType = 'image/webp') {
  const safeName = filename || 'prize-image';
  const extension = mimeType === 'image/jpeg' ? '.jpg' : mimeType === 'image/png' ? '.png' : '.webp';
  return /\.(jpe?g|png|webp)$/i.test(safeName)
    ? safeName.replace(/\.[^.]+$/, extension)
    : `${safeName}${extension}`;
}

function loadImageElement(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`${file.name} 图片读取失败`));
    };
    image.src = url;
  });
}

function canvasToBlob(canvas, mimeType, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('图片压缩失败'));
      }
    }, mimeType, quality);
  });
}

async function compressImageFile(file) {
  const image = await loadImageElement(file);
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  const scale = Math.min(1, COMPRESS_MAX_EDGE / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));

  const context = canvas.getContext('2d');
  if (!context) throw new Error('浏览器不支持图片压缩');
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  let bestBlob = null;
  for (
    let quality = COMPRESS_START_QUALITY;
    quality >= COMPRESS_MIN_QUALITY - 0.001;
    quality -= COMPRESS_QUALITY_STEP
  ) {
    const blob = await canvasToBlob(canvas, 'image/webp', Number(quality.toFixed(2)));
    if (!bestBlob || blob.size < bestBlob.size) bestBlob = blob;
    if (blob.size <= COMPRESS_TARGET_BYTES) break;
  }

  if (!bestBlob) throw new Error('图片压缩失败');
  return {
    blob: bestBlob,
    filename: getCompressedFilename(file.name, bestBlob.type)
  };
}

export async function prepareImageFile(file) {
  if (!isCompressibleImage(file) || file.size <= COMPRESS_TARGET_BYTES) {
    return readImageFile(file);
  }

  try {
    const compressed = await compressImageFile(file);
    if (compressed.blob.size <= MAX_IMAGE_BYTES && compressed.blob.size < file.size) {
      return readImageBlob(compressed.blob, compressed.filename);
    }
  } catch (err) {
    if (file.size > MAX_IMAGE_BYTES) throw err;
  }

  if (file.size > MAX_IMAGE_BYTES) {
    throw new Error(`${file.name} 压缩后仍超过 5MB，请换一张更小的图片`);
  }
  return readImageFile(file);
}

export function validateImageFiles(files) {
  const notImage = files.find((file) => file.type && !file.type.startsWith('image/'));
  if (notImage) {
    throw new Error(`${notImage.name} 不是支持的图片文件`);
  }

  const oversizedSource = files.find((file) => file.size > MAX_SOURCE_IMAGE_BYTES);
  if (oversizedSource) {
    throw new Error(`${oversizedSource.name} 超过 20MB，请先压缩后再上传`);
  }

  const oversized = files.find((file) => !isCompressibleImage(file) && file.size > MAX_IMAGE_BYTES);
  if (oversized) {
    throw new Error(`${oversized.name} 超过 5MB，gif 等动图请先压缩后再上传`);
  }
}

export function getUploadErrorMessage(err) {
  if (err.response?.status === 413) {
    return '图片请求被服务器拦截了。请确认 HTTPS 的 Nginx server 配置了 client_max_body_size 50m，并重载 Nginx';
  }
  return err.response?.data?.message || err.message;
}

export function PrizeBasicFields({ form, setForm }) {
  const activeOptions = (form.options || []).filter((option) => option.is_active);
  const optionStockTotal = activeOptions.reduce((sum, option) => sum + Number(option.stock || 0), 0);

  return (
    <>
      <label>
        名称
        <input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required />
      </label>
      <label>
        描述
        <textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} rows={4} />
      </label>
      <div className="prize-form-grid">
        <div className="delivery-type-field">
          <span>商品类型</span>
          <div className="delivery-type-toggle" role="group" aria-label="商品类型">
            <button
              type="button"
              className={(form.delivery_type || 'physical') === 'physical' ? 'active physical' : 'physical'}
              onClick={() => setForm({ ...form, delivery_type: 'physical' })}
            >
              实体商品
            </button>
            <button
              type="button"
              className={form.delivery_type === 'virtual' ? 'active virtual' : 'virtual'}
              onClick={() => setForm({ ...form, delivery_type: 'virtual' })}
            >
              虚拟商品
            </button>
          </div>
        </div>
        <label>
          默认积分价
          <input type="number" min="0" value={form.cost} onChange={(event) => setForm({ ...form, cost: event.target.value })} placeholder="留空不开放" />
        </label>
        <label>
          默认电影票价
          <input type="number" min="0" value={form.movie_ticket_cost} onChange={(event) => setForm({ ...form, movie_ticket_cost: event.target.value })} placeholder="留空不开放" />
        </label>
        {activeOptions.length > 0 ? (
          <div className="stock-summary-field">
            <span>库存</span>
            <strong>{optionStockTotal}</strong>
            <small>{activeOptions.length} 个启用子选项库存合计</small>
          </div>
        ) : (
          <label>
            库存
            <input type="number" min="0" value={form.stock} onChange={(event) => setForm({ ...form, stock: event.target.value })} required />
          </label>
        )}
      </div>
    </>
  );
}

export function PrizeStatusFields({ form, setForm }) {
  return (
    <>
      <label className="toggle-line">
        <input type="checkbox" checked={form.is_active} onChange={(event) => setForm({ ...form, is_active: event.target.checked })} />
        上架显示
      </label>
      <label className="toggle-line">
        <input type="checkbox" checked={form.auto_carousel} onChange={(event) => setForm({ ...form, auto_carousel: event.target.checked })} />
        自动轮播图片
      </label>
    </>
  );
}

export function PrizeOptionsSummary({ form, onOpenFullEditor, onAddOption }) {
  const options = form.options || [];

  return (
    <section className="prize-options-editor prize-options-summary-card">
      <div className="panel-toolbar compact-toolbar">
        <div>
          <h3>子选项</h3>
          <p>复杂规格会在完整编辑页管理，避免侧栏表单过长。</p>
        </div>
      </div>
      {options.length > 0 ? (
        <div className="option-summary-list">
          {options.slice(0, 3).map((option, index) => (
            <span key={option.id || index}>{option.name || `选项 ${index + 1}`}</span>
          ))}
          {options.length > 3 && <span>+{options.length - 3}</span>}
        </div>
      ) : (
        <div className="empty-inline">未设置子选项，用户会直接兑换这个礼物。</div>
      )}
      <div className="option-summary-actions">
        <button type="button" className="secondary-action" onClick={onOpenFullEditor}>进入完整编辑</button>
        <button type="button" className="secondary-action" onClick={onAddOption}>添加子选项</button>
      </div>
    </section>
  );
}

const getPreviewImageUrl = (url) => {
  if (!url) return '';
  if (/^data:/i.test(url)) return url;
  return getImageUrl(url);
};

export function PrizeOptionsEditor({ form, setForm, onError = () => {}, hasParentImages = true }) {
  const updateOption = (index, patch) => {
    setForm((current) => {
      const options = [...(current.options || [])];
      options[index] = { ...options[index], ...patch };
      return { ...current, options };
    });
  };

  const addOption = () => {
    setForm((current) => ({
      ...current,
      options: [...(current.options || []), createEmptyOption()]
    }));
  };

  const removeOption = (index) => {
    setForm((current) => ({
      ...current,
      options: (current.options || []).filter((_, optionIndex) => optionIndex !== index)
    }));
  };

  const moveOption = (index, direction) => {
    setForm((current) => {
      const options = [...(current.options || [])];
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= options.length) return current;
      [options[index], options[nextIndex]] = [options[nextIndex], options[index]];
      return { ...current, options };
    });
  };

  const handleOptionImageSelect = async (index, event) => {
    const [file] = Array.from(event.target.files || []);
    if (!file) return;

    try {
      validateImageFiles([file]);
      const image = await prepareImageFile(file);
      updateOption(index, {
        image,
        image_preview_url: image.data_url,
        remove_image: false
      });
    } catch (err) {
      onError(getUploadErrorMessage(err) || '读取子选项图片失败');
    } finally {
      event.target.value = '';
    }
  };

  const removeOptionImage = (index) => {
    updateOption(index, {
      image: null,
      image_url: '',
      image_preview_url: '',
      remove_image: true
    });
  };

  return (
    <section className="prize-options-editor">
      <div className="panel-toolbar compact-toolbar">
        <div>
          <h3>子选项</h3>
          <p>可用于口味、规格等；价格留空会继承上方商品默认价。</p>
        </div>
        <button type="button" className="secondary-action" onClick={addOption}>添加选项</button>
      </div>

      {(form.options || []).length === 0 ? (
        <div className="empty-inline">未设置子选项，用户会直接兑换这个礼物。</div>
      ) : (
        <div className="prize-option-editor-list">
          {(form.options || []).map((option, index) => (
            <div key={option.id || index} className="prize-option-editor-row">
              <div className="option-row-header">
                <strong>选项 {index + 1}</strong>
                <div className="row-actions">
                  <button type="button" onClick={() => moveOption(index, -1)} disabled={index === 0}>上移</button>
                  <button type="button" onClick={() => moveOption(index, 1)} disabled={index === (form.options || []).length - 1}>下移</button>
                  <button type="button" className="danger-text" onClick={() => removeOption(index)}>删除</button>
                </div>
              </div>
              <div className="prize-option-image-row">
                <div className="prize-option-image-preview">
                  {getPreviewImageUrl(option.image_preview_url || option.image_url) ? (
                    <img src={getPreviewImageUrl(option.image_preview_url || option.image_url)} alt={option.name || `选项 ${index + 1}`} />
                  ) : (
                    <span>{hasParentImages ? '继承商品图片' : '无商品图可继承'}</span>
                  )}
                </div>
                <div className="prize-option-image-actions">
                  <label className="file-button">
                    {option.image_url || option.image_preview_url ? '替换选项图片' : '上传选项图片'}
                    <input type="file" accept={IMAGE_ACCEPT} onChange={(event) => handleOptionImageSelect(index, event)} />
                  </label>
                  {(option.image_url || option.image_preview_url) && (
                    <button type="button" className="danger-action" onClick={() => removeOptionImage(index)}>
                      移除选项图片
                    </button>
                  )}
                </div>
              </div>
              <div className="prize-option-compact-grid">
                <label>
                  选项名称
                  <input value={option.name} onChange={(event) => updateOption(index, { name: event.target.value })} placeholder="例如：草莓味" />
                </label>
                <label>
                  独立库存
                  <input type="number" min="0" value={option.stock} onChange={(event) => updateOption(index, { stock: event.target.value })} placeholder="0" />
                </label>
                <label>
                  积分价格
                  <input type="number" min="0" value={option.cost} onChange={(event) => updateOption(index, { cost: event.target.value })} placeholder="留空继承" />
                </label>
                <label>
                  电影票价格
                  <input type="number" min="0" value={option.movie_ticket_cost} onChange={(event) => updateOption(index, { movie_ticket_cost: event.target.value })} placeholder="留空继承" />
                </label>
                <label>
                  选项描述
                  <textarea value={option.description} onChange={(event) => updateOption(index, { description: event.target.value })} rows={1} placeholder="可选备注或展示说明" />
                </label>
                <label className="toggle-line prize-option-toggle">
                  <input type="checkbox" checked={option.is_active} onChange={(event) => updateOption(index, { is_active: event.target.checked })} />
                  启用
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
